import { create } from "zustand";

import type { Correspondent } from "../features/messages/api";

/**
 * Which conversations are docked, and which are open.
 *
 * ## Why this is a store and not state in the dock
 *
 * The dock bar is mounted in the shell, outside the router, so it already
 * survives navigation - but the pages that *start* a conversation do not. A
 * profile's "Message" button opens a window in a bar that outlives the profile,
 * and it needs somewhere to say so that is not a prop drilled through the
 * router.
 *
 * ## In memory, deliberately
 *
 * §9.2 asks for the minimised state to survive navigation, which this does by
 * living above the routes. It does not ask for it to survive a reload, and
 * restoring four open chat windows onto a fresh page load is a decision nobody
 * made - a reload is how people clear things away.
 */

/** How many tabs fit before the rail starts crowding the player. */
const MAX_TABS = 4;

/** §9.2: a window is 320 wide, and they sit 8 apart with 16 clear of the edge. */
const PANEL = 320;
const GAP = 8;
const EDGE = 16;

/**
 * How many windows fit side by side without overlapping.
 *
 * Open windows tile from the right edge of the bar rather than each hanging off
 * its own tab. Anchoring them to their tabs is what made them overlap: a tab is
 * about 150px wide and a window is 320, so any two open at once covered each
 * other, and the one underneath was unreachable without collapsing the one on
 * top.
 *
 * Read at call time rather than stored, because the answer changes with the
 * window and a stale capacity is how you get the overlap back.
 */
function capacity(): number {
  if (typeof window === "undefined") return 1;

  return Math.max(1, Math.floor((window.innerWidth - EDGE) / (PANEL + GAP)));
}

interface ChatState {
  /** Docked conversations, newest at the right-hand end of the rail. */
  tabs: number[];
  /** The ones expanded into a window rather than collapsed to a tab. */
  open: number[];

  /**
   * Who each docked conversation is with.
   *
   * The rail used to read names and avatars only out of the shared
   * `conversations` query, which does not have a conversation that was created
   * a moment ago and has no messages in it - so messaging somebody new docked a
   * tab labelled "Conversation" with a blank avatar. Seeding that query was not
   * enough either: the refetch behind it returns a list the new conversation is
   * not in yet and puts the placeholder straight back.
   *
   * So whoever docks a conversation says who it is with, and the inbox is left
   * to do what it is good at - unread counts and previews.
   */
  people: Record<number, Correspondent>;

  /** Dock a conversation and expand it, which is what "message them" means. */
  start: (conversationId: number, other?: Correspondent | null) => void;
  /** Collapse to a tab, or expand from one. */
  toggle: (conversationId: number) => void;
  /** Undock entirely. */
  close: (conversationId: number) => void;
  /** Collapse whatever no longer fits, after a resize. */
  fit: () => void;
}

export const useChatDock = create<ChatState>()((set) => ({
  tabs: [],
  open: [],
  people: {},

  start: (id, other) =>
    set((state) => ({
      people: other ? { ...state.people, [id]: other } : state.people,
      /*
       * Moved to the end rather than duplicated, so messaging somebody twice
       * brings their tab back to where the newest one is instead of adding a
       * second of the same person.
       */
      tabs: [...state.tabs.filter((t) => t !== id), id].slice(-MAX_TABS),
      /*
       * Oldest window out when there is no room for another.
       *
       * It stays docked as a tab - the conversation is not over, it is just not
       * the one being looked at - which is the difference between running out
       * of space and being closed.
       */
      open: [...state.open.filter((t) => t !== id), id].slice(-capacity()),
    })),

  toggle: (id) =>
    set((state) => ({
      tabs: state.tabs.includes(id) ? state.tabs : [...state.tabs, id].slice(-MAX_TABS),
      open: state.open.includes(id)
        ? state.open.filter((t) => t !== id)
        : [...state.open, id].slice(-capacity()),
    })),

  close: (id) =>
    set((state) => ({
      tabs: state.tabs.filter((t) => t !== id),
      open: state.open.filter((t) => t !== id),
    })),

  fit: () => set((state) => ({ open: state.open.slice(-capacity()) })),
}));
